import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { debounce } from "../../commonFunctions/arrayFunc";
import CopyToClipboard from "../../components/BuilderComponents/CopyToClipboard";
import MaterialIcon from "../../components/BuilderComponents/MaterialIcon";
import Button from "../../components/FormComponents/Button";
import Input from "../../components/FormComponents/Input";
import SingleSelectBox from "../../components/FormComponents/SingleSelectBox";
import { STORAGE_KEYS } from "../../constant/constant";
import { useModalContext } from "../../context/ModalContext";
import Loader from "../../Layouts/Loader/Loader";
import axiosInstance, { handleApiError } from "../../services/axiosService";
import storageService from "../../services/storageService";
import { toastSuccess } from "../../utils/toastUtils";
import Pagination from "../ApplicationUsers/Pagination";

const ApplicationHealth = () => {
  const [appList, setAppList] = useState([]);
  const [versionList, setVersionList] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  const [pageTrack, setPageTrack] = useState({
    currPage: undefined,
    totalPages: undefined
  });
  const limit = storageService.get(STORAGE_KEYS.LIMIT) || 10;

  const [pagination, setPagination] = useState({
    search: undefined,
    page: 1,
    limit: Number(limit)
  });
  const navigate = useNavigate();

  const getAllApps = async (page = 1, limit = 10, search) => {
    try {
      const { data } = await axiosInstance.get(`/applications`, {
        params: {
          page: page,
          limit: limit,
          search: search
        }
      });

      const { data: version } = await axiosInstance.get(`/application/version`);
      setVersionList(version);
      setPageTrack({
        currPage: data.pagination.current_page,
        totalPages: data.pagination.total_pages
      });

      setAppList(data.apps);
    } catch (error) {
      handleApiError(error);
    }
  };

  useEffect(() => {
    getAllApps(1, 10);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function getStatusColor(status) {
    if (status === 0) return "";
    if (status === 200) return "#2ab32a";
    else return "#f64343";
  }

  function getStatusClassName(status) {
    if (status === 0) return "";
    else return "app_status_chip";
  }

  const changeAppVersion = async (newVersion, app) => {
    try {
      const { data } = await axiosInstance.put(`/application/version/${newVersion.id}`, null, {
        headers: {
          "Zino-App-Id": app.id
        }
      });

      const updatedAppList = appList.map((item) => {
        if (item.id === app.id) {
          return { ...item, version: newVersion.version };
        }
        return item;
      });

      setAppList(updatedAppList);
      toastSuccess(data.message || "Version updated successfully");
    } catch (error) {
      handleApiError(error);
    }
  };

  const updateAppList = (appId, status, currentVersion) => {
    setAppList((prevAppList) => {
      const updatedAppList = prevAppList.map((item) => {
        if (item.id === appId) {
          return {
            ...item,
            status: status,
            current_version: currentVersion
          };
        }
        return item;
      });
      return updatedAppList;
    });
  };

  // Filtered apps based on search query
  const filteredApps = (appList || []).filter(
    (app) =>
      app.id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      app.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      app.organisation?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      app.current_version?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Function to handle page changes in pagination
  const onPageChangeAPI = async (pageNumber) => {
    try {
      const { limit, search } = pagination;
      await getAllApps(pageNumber, limit, search);
      setPagination((prev) => ({ ...prev, page: pageNumber }));
    } catch (error) {
      const errorMessage = error.response?.data?.message || "Failed to fetch applications";
      console.error(errorMessage);
    }
  };

  // Function to handle limit changes in pagination
  const onLimitChangeAPI = async (newLimit) => {
    try {
      storageService.set(STORAGE_KEYS.LIMIT, newLimit);
      const { page, search } = pagination;
      await getAllApps(page, newLimit, search);
      setPagination((prev) => ({ ...prev, limit: newLimit }));
    } catch (error) {
      const errorMessage = error.response?.data?.message || "Failed to fetch applications";
      console.error(errorMessage);
    }
  };

  // Simplified debounced search function
  const debouncedSearch = useMemo(
    () =>
      debounce((query) => {
        if (!query || query.trim().length < 1) {
          getAllApps(1, pagination.limit);
          return;
        }
        getAllApps(1, pagination.limit, query);
      }, 500),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pagination.limit]
  );

  const handleSearchInputChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    setPagination((prev) => ({ ...prev, search: query, page: 1 }));
    debouncedSearch(query);
  };

  const navigateToApplication = (orgId, appId) => {
    navigate(`/organization/${orgId}/app/${appId}/screen`);
  };
  const navigateToOrganization = (orgId) => {
    navigate(`/organization/${orgId}/app`);
  };

  return (
    <div className="flex_column pt_4">
      <div className="top_border">
        <div className="d_flex justify_between mb_2 p_3" style={{ width: "300px" }}>
          <Input
            placeholder="Search by App Id, App name, Org and Version"
            value={searchQuery}
            onChange={handleSearchInputChange}
            containerStyle={{ position: "relative", width: "80%" }}
            inputBoxStyle={{ paddingLeft: "28px", height: "36px" }}
          />
          <MaterialIcon
            name="search"
            className="position_relative font_size_large"
            style={{ top: "8px", left: "-240px" }}
          />
        </div>
      </div>

      <div className="user_table">
        <table>
          <thead style={{ zIndex: "5" }}>
            <tr>
              <th>Application</th>
              <th>Organisation</th>
              <th>Set Version</th>
              <th>Current Version</th>
              <th>Api Status</th>
              <th>Mongo Status</th>
              <th>Rdbms Status</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredApps.length > 0 ? (
              filteredApps.map((app) => (
                <tr key={app.id}>
                  <td>
                    {/* <div
                      className="clustor_manager_item d_flex align_center gap_2"
                      onClick={() => app.name && navigateToApplication(app.org_id, app.id)}
                      title={app.id}
                    >
                      <span className="clustor_manager_name">{app.name}</span>
                      <MaterialIcon
                        name="content_copy"
                        className="font_size_small cursor_pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          copyToClipboard(app.id);
                        }}
                      />
                      {app.name && (
                        <MaterialIcon
                          name="north_east"
                          className="font_size_small cursor_pointer clustor_manager_hover_icon"
                        />
                      )}
                    </div> */}
                    <ClustorManagerItem app={app} navigateToApplication={navigateToApplication} />
                  </td>
                  <td>
                    <div
                      className="clustor_manager_item d_flex align_center gap_2"
                      onClick={() => app.organisation && navigateToOrganization(app.org_id)}
                    >
                      <span className="clustor_manager_name">{app.organisation}</span>
                      <MaterialIcon
                        name="north_east"
                        className="font_size_small cursor_pointer clustor_manager_hover_icon"
                      />
                    </div>
                  </td>
                  <td>
                    <SingleSelectBox
                      selectedData={app.version}
                      labelKey={["version"]}
                      valueKey={"version"}
                      options={versionList}
                      onChange={(obj) => changeAppVersion(obj, app)}
                    />
                  </td>
                  <td>{app.current_version ? app.current_version : "----"}</td>
                  <td>
                    <span
                      className={getStatusClassName(app.health.api_status)}
                      style={{ backgroundColor: getStatusColor(app.health.api_status) }}
                    >
                      {app.health.api_status}
                    </span>
                  </td>
                  <td>
                    <span
                      className={getStatusClassName(app.health.mongo_status)}
                      style={{ backgroundColor: getStatusColor(app.health.mongo_status) }}
                    >
                      {app.health.mongo_status}
                    </span>
                  </td>
                  <td>
                    <span
                      className={getStatusClassName(app.health.rdbms_status)}
                      style={{ backgroundColor: getStatusColor(app.health.rdbms_status) }}
                    >
                      {app.health.rdbms_status}
                    </span>
                  </td>
                  <td>
                    <span
                      className={getStatusClassName(app.status)}
                      style={{ backgroundColor: getStatusColor(app.status) }}
                    >
                      {app.status || 0}
                    </span>
                  </td>
                  <td>
                    <StartActionButton app={app} updateAppList={updateAppList} />
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="9">No App Found</td>
              </tr>
            )}
          </tbody>
        </table>
        <div className="p_4 bottom_border">
          <Pagination
            pageTrack={pageTrack}
            pagination={pagination}
            onPageChange={onPageChangeAPI}
            onLimitChange={onLimitChangeAPI}
          />
        </div>
      </div>
    </div>
  );
};
const ClustorManagerItem = ({ app, navigateToApplication }) => {
  const handleNavigate = (e) => {
    e.stopPropagation();
    if (app.name) {
      navigateToApplication(app.org_id, app.id);
    }
  };

  return (
    <div className="clustor_manager_item d_flex align_center gap_2" onClick={handleNavigate} title={app.id}>
      <span className="clustor_manager_name">{app.name}</span>

      <CopyToClipboard
        text={app.id}
        successMessage="App ID copied"
        errorMessage="Failed to copy App ID"
        defaultIcon="content_copy"
        copiedIcon="check_circle"
        resetDelay={2000}
      />

      {app.name && (
        <MaterialIcon name="north_east" className="font_size_small cursor_pointer clustor_manager_hover_icon" />
      )}
    </div>
  );
};

const StartActionButton = ({ app, updateAppList }) => {
  const [statusData, setStatusData] = useState({
    status: app.status,
    current_version: app.current_version
  });
  const { getUserConfirmationForDelete } = useModalContext();

  const controller = new AbortController();

  const fetchStatus = async () => {
    try {
      // const data = await fetchAppInfraStatus(app);
      const { data } = await axiosInstance.get(`/application/infra`, {
        signal: controller.signal,
        timeout: 50000,
        headers: {
          "Zino-App-Id": app.id
        }
      });
      setStatusData({
        status: data?.status,
        current_version: data?.current_version
      });
      updateAppList(app.id, data.status, data.current_version);
    } catch (error) {
      handleApiError(error);
    }
  };

  const createAction = async (e, id) => {
    e.stopPropagation();
    const response = await axiosInstance.post(`/application/infra`, null, {
      headers: {
        "Zino-App-Id": id
      }
    });
    toastSuccess(response.message);
  };

  const destroyAction = async (e, id, name) => {
    try {
      e.stopPropagation();
      const userConfirmed = await getUserConfirmationForDelete(name, "", "stop");
      if (!userConfirmed) return;

      const response = await axiosInstance.delete(`/application/infra`, {
        headers: {
          "Zino-App-Id": id
        }
      });
      toastSuccess(response.message);
    } catch (error) {
      handleApiError(error);
    }
  };
  const createApplicationLink = async (e) => {
    try {
      await createAction(e, app.id);
      setStatusData({
        ...statusData,
        status: "starting"
      });
    } catch (error) {
      handleApiError(error);
    }
  };

  const destroyApplicationLink = async (e) => {
    try {
      await destroyAction(e, app.id, app.name);
      await fetchStatus();
    } catch (error) {
      handleApiError(error);
    }
  };

  useEffect(() => {
    let intervalId;

    if (statusData.status === "starting") {
      intervalId = setInterval(fetchStatus, 5000);
    }
    if (statusData.status === "publishing") {
      intervalId = setInterval(fetchStatus, 5000);
    }
    if (statusData.status === "stopping") {
      intervalId = setInterval(fetchStatus, 5000);
    }

    return () => {
      clearInterval(intervalId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusData.status]);

  return (
    <>
      <td>
        <div className={`app_status ${statusData?.status?.toLowerCase()}`}>{statusData?.status}</div>
      </td>
      <td>
        {statusData.status === "stopped" && (
          <Button size="small" variant="secondary" className="border_radius_small" onClick={createApplicationLink}>
            Start
          </Button>
        )}
        {statusData.status === "starting" && (
          <button className="app_action_button" disabled={true}>
            <Loader width="10px" />
          </button>
        )}
        {statusData.status === "live" && (
          <Button size="small" variant="secondary" className="border_radius_small" onClick={destroyApplicationLink}>
            Stop
          </Button>
        )}
      </td>
    </>
  );
};

export default ApplicationHealth;
